import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Footer from './Footer';
import { useSidebar } from '../context/SidebarContext';
import { getNavigationForRole, getEmployeeSelfServiceNav } from '../utils/roleBasedAccess';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function Layout({ children, user, onLogout }) {
  const { sidebarOpen, toggleSidebar, expandedMenus, toggleMenu } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [taskCount, setTaskCount] = useState(0);
  const [emailCount, setEmailCount] = useState(0);
  const [showTaskDropdown, setShowTaskDropdown] = useState(false);
  const [myTasks, setMyTasks] = useState([]);
  const sidebarScrollRef = useRef(null);

  // Fetch notification count
  useEffect(() => {
    fetchNotificationCount();
    fetchMyTaskCount();
    fetchEmailCount();
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchNotificationCount();
      fetchMyTaskCount();
      fetchEmailCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotificationDropdown && !event.target.closest('.notification-dropdown-container')) {
        setShowNotificationDropdown(false);
      }
      if (showTaskDropdown && !event.target.closest('.task-dropdown-container')) {
        setShowTaskDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationDropdown, showTaskDropdown]);

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch unread notifications count
      const response = await axios.get(`${API}/api/notifications?unread=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Array.isArray(response.data)) {
        setNotificationCount(response.data.length);
        setNotifications(response.data.slice(0, 5)); // Get top 5 for dropdown
      }
    } catch (error) {
      // Silently fail - notifications are not critical
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchMyTaskCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/my-tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTaskCount(response.data.total_tasks || 0);
        // Get pending tasks (not done)
        const allTasks = response.data.boards?.flatMap(b => b.tasks) || [];
        const pendingTasks = allTasks.filter(t => t.status !== 'done').slice(0, 5);
        setMyTasks(pendingTasks);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchEmailCount = async () => {
    try {
      const token = localStorage.getItem('token');
      // Count email-type notifications (task_assigned, leave_approved, etc.)
      const response = await axios.get(`${API}/api/notifications?unread=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Array.isArray(response.data)) {
        const emailNotifs = response.data.filter(n => 
          ['task_assigned', 'leave_approved', 'leave_rejected', 'announcement', 'payslip'].includes(n.type)
        );
        setEmailCount(emailNotifs.length);
      }
    } catch (error) {
      console.error('Failed to fetch email count:', error);
    }
  };

  const handleNotificationClick = () => {
    setShowNotificationDropdown(!showNotificationDropdown);
    setShowTaskDropdown(false);
  };

  const handleTaskClick = () => {
    setShowTaskDropdown(!showTaskDropdown);
    setShowNotificationDropdown(false);
  };

  const handleViewAllNotifications = () => {
    setShowNotificationDropdown(false);
    navigate('/notifications');
  };

  // Preserve sidebar scroll position on navigation
  useEffect(() => {
    if (sidebarScrollRef.current) {
      const scrollPosition = sessionStorage.getItem('sidebarScrollPosition');
      if (scrollPosition) {
        sidebarScrollRef.current.scrollTop = parseInt(scrollPosition, 10);
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (sidebarScrollRef.current) {
        sessionStorage.setItem('sidebarScrollPosition', sidebarScrollRef.current.scrollTop.toString());
      }
    };

    const scrollElement = sidebarScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => scrollElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Full navigation (for Admin) - Organized with clear hierarchy
  const fullNavigation = [
    // ═══════════════════════════════════════════════
    // CORE MODULES
    // ═══════════════════════════════════════════════
    { name: 'Dashboard', path: '/', icon: '📊' },
    { name: 'Approvals', path: '/approvals', icon: '✅' },
    
    // ═══════════════════════════════════════════════
    // PRODUCTIVITY & COLLABORATION
    // ═══════════════════════════════════════════════
    { 
      name: 'Productivity', 
      path: '/productivity', 
      icon: '⚡',
      subItems: [
        { name: 'Tasks', path: '/tasks', icon: '✅' },
        { name: 'Email', path: '/email', icon: '📧' },
        { name: 'Drive', path: '/drive', icon: '💾' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // CRM & CUSTOMERS
    // ═══════════════════════════════════════════════
    { 
      name: 'CRM & Sales', 
      path: '/crm', 
      icon: '📞',
      subItems: [
        { name: 'Customer 360°', path: '/customer-360', icon: '🎯' },
        { name: 'All Contacts', path: '/crm', icon: '👥' },
        { name: 'Call Center', path: '/call-center', icon: '☎️' },
        { name: 'Express IVR Calls', path: '/express-ivr-call-center', icon: '📞' },
        { name: 'B2C Customers', path: '/customer-dashboard?type=b2c', icon: '🛍️' },
        { name: 'B2R Retailers', path: '/customer-dashboard?type=b2r', icon: '🏪' },
        { name: 'B2B Buyers', path: '/customer-dashboard?type=b2b', icon: '🏢' },
        { name: 'Partners', path: '/customer-dashboard?type=partner', icon: '🤝' },
      ]
    },
    { 
      name: 'WhatsApp', 
      path: '/whatsapp', 
      icon: '💬',
      subItems: [
        { name: 'Team Inbox', path: '/whatsapp-inbox', icon: '📥' },
        { name: 'Contacts', path: '/whatsapp-contacts', icon: '👥' },
        { name: 'Campaigns', path: '/whatsapp-campaigns', icon: '📢' },
        { name: 'Quick Replies', path: '/whatsapp-canned-responses', icon: '⚡' },
        { name: 'AI Chatbot', path: '/whatsapp-chatbot', icon: '🤖' },
        { name: 'Analytics', path: '/whatsapp-analytics', icon: '📊' },
        { name: 'Website Widget', path: '/whatsapp-widget', icon: '🌐' },
        { name: 'Automation', path: '/whatsapp-automation', icon: '🔧' },
        { name: 'Settings', path: '/whatsapp-settings', icon: '⚙️' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // HUMAN RESOURCES
    // ═══════════════════════════════════════════════
    {
      name: 'HRMS',
      path: '/hrms',
      icon: '👥',
      subItems: [
        { name: 'HR Dashboard', path: '/hrms', icon: '📊' },
        { name: '━━ RECRUITMENT ━━', path: null, icon: '', disabled: true },
        { name: 'Unified Joining Flow', path: '/unified-joining-flow', icon: '🎯' },
        { name: 'Job Postings', path: '/recruitment/jobs', icon: '📢' },
        { name: 'Candidate Pipeline', path: '/recruitment/candidates', icon: '👤' },
        { name: 'Screening', path: '/recruitment/screening', icon: '🔍' },
        { name: 'Interviews', path: '/recruitment/interviews', icon: '🎤' },
        { name: '━━ ONBOARDING ━━', path: null, icon: '', disabled: true },
        { name: 'Onboarding Dashboard', path: '/onboarding-dashboard', icon: '📊' },
        { name: '━━ EMPLOYEES ━━', path: null, icon: '', disabled: true },
        { name: 'All Employees', path: '/employees', icon: '👥' },
        { name: 'Users & Roles', path: '/roles', icon: '👤' },
        { name: '━━ TIME & ATTENDANCE ━━', path: null, icon: '', disabled: true },
        { name: 'Attendance', path: '/attendance', icon: '📅' },
        { name: 'Field Attendance', path: '/offsite-attendance', icon: '📍' },
        { name: 'Attendance Regularization', path: '/attendance-regularization', icon: '🔄' },
        { name: 'Leave Requests', path: '/leaves', icon: '🏖️' },
        { name: 'Leave Balance', path: '/leave-balance-allocation', icon: '📋' },
        { name: '━━ PAYROLL ━━', path: null, icon: '', disabled: true },
        { name: 'Payslips', path: '/payslips', icon: '💰' },
        { name: 'Payslip Management', path: '/payslip-management', icon: '🧾' },
        { name: 'Travel & Expense Claims', path: '/claims-management', icon: '💳' },
        { name: '━━ PERFORMANCE ━━', path: null, icon: '', disabled: true },
        { name: 'Performance Management', path: '/performance', icon: '⭐' },
        { name: '━━ EXIT ━━', path: null, icon: '', disabled: true },
        { name: 'Exit Management', path: '/exit-management', icon: '🚪' },
        { name: '━━ ASSETS ━━', path: null, icon: '', disabled: true },
        { name: 'Asset Dashboard', path: '/assets/dashboard', icon: '📊' },
        { name: 'All Assets', path: '/assets', icon: '🖥️' },
        { name: 'Asset Allocation', path: '/assets/allocation', icon: '📤' },
        { name: '━━ HR ADMIN ━━', path: null, icon: '', disabled: true },
        { name: 'HR Settings', path: '/hr-settings', icon: '⚙️' },
        { name: 'Company Policies', path: '/company-policies', icon: '📜' },
        { name: 'Document Generation', path: '/document-generation', icon: '📄' },
        { name: 'Noticeboard', path: '/noticeboard', icon: '📌' },
        { name: 'Bulk Upload', path: '/bulk-upload', icon: '📤' },
        { name: 'Compliance Reports', path: '/compliance-reports', icon: '📊' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // PRODUCTS & INVENTORY
    // ═══════════════════════════════════════════════
    { 
      name: 'Products', 
      path: '/products', 
      icon: '📱',
      subItems: [
        { name: 'Products (SKU)', path: '/products?tab=products', icon: '📦' },
        { name: 'Brand Master', path: '/brands', icon: '🏷️' },
        { name: 'Variants (ZUPC)', path: '/products?tab=variants', icon: '🔢' },
        { name: 'Pricing', path: '/pricing', icon: '💰' },
      ]
    },
    { 
      name: 'Inventory', 
      path: '/inventory', 
      icon: '📦',
      subItems: [
        { name: 'Stock Overview', path: '/inventory', icon: '📊' },
        { name: 'Warehouses', path: '/warehouses', icon: '🏭' },
        { name: 'Stock Movements', path: '/stock-movements', icon: '🔄' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // PROCUREMENT & SUPPLY CHAIN
    // ═══════════════════════════════════════════════
    { 
      name: 'Suppliers', 
      path: '/supplier-management', 
      icon: '🏢',
      subItems: [
        { name: 'VRF Applications', path: '/supplier-management?tab=vrfs', icon: '📋' },
        { name: 'All Suppliers', path: '/supplier-management?tab=suppliers', icon: '🏢' },
      ]
    },
    { 
      name: 'Procurement', 
      path: '/purchase-management', 
      icon: '🛒',
      subItems: [
        { name: 'Product Purchase', path: '/product-purchase', icon: '📱' },
        { name: 'Spare Parts Purchase', path: '/spare-parts-purchase', icon: '🔧' },
        { name: 'Requisitions', path: '/requisitions', icon: '📋' },
        { name: 'RFQ & Quotations', path: '/purchase-management?tab=quotations', icon: '💰' },
        { name: 'Purchase Orders', path: '/purchase-management?tab=po', icon: '📄' },
        { name: 'GRN', path: '/purchase-management?tab=grn', icon: '📦' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // QC MANAGEMENT (New Standalone Module)
    // ═══════════════════════════════════════════════
    { 
      name: 'QC Management', 
      path: '/qc', 
      icon: '🎯',
      subItems: [
        { name: '━━ MASTER CONFIG ━━', path: null, icon: '', disabled: true },
        { name: 'QC Control Hub', path: '/qc/control', icon: '🎛️' },
        { name: '━━ CQC (CUSTOMER) ━━', path: null, icon: '', disabled: true },
        { name: 'CQC Flow (17-Screen)', path: '/sell-phone', icon: '📱', external: true },
        { name: 'CQC Configuration', path: '/qc/cqc-config', icon: '⚙️' },
        { name: '━━ OTHER STAGES ━━', path: null, icon: '', disabled: true },
        { name: 'PQC Config', path: '/qc/pqc-config', icon: '🚚' },
        { name: 'IQC Config', path: '/qc/iqc-config', icon: '🔧' },
        { name: 'FQC Config', path: '/qc/fqc-config', icon: '✅' },
        { name: 'OQC Config', path: '/qc/oqc-config', icon: '📦' },
        { name: '━━ QUEUES ━━', path: null, icon: '', disabled: true },
        { name: 'IQC Queue', path: '/qc/iqc-queue', icon: '📋' },
        { name: 'FQC Queue', path: '/qc/fqc-queue', icon: '📋' },
        { name: 'Laptop IQC', path: '/qc/laptop-iqc', icon: '💻' },
        { name: '━━ ANALYTICS ━━', path: null, icon: '', disabled: true },
        { name: 'QC Dashboard', path: '/qc/dashboard', icon: '📊' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // TRC (Technical Repair Center)
    // ═══════════════════════════════════════════════
    { 
      name: 'TRC', 
      path: '/trc', 
      icon: '🔧',
      subItems: [
        { name: '━━ INVENTORY ━━', path: null, icon: '', disabled: true },
        { name: 'TRC Dashboard', path: '/trc/inventory/dashboard', icon: '📊' },
        { name: 'Create STN', path: '/trc/inventory/create-stn', icon: '📝' },
        { name: 'Create SRN', path: '/trc/inventory/create-srn', icon: '📥' },
        { name: 'Store Inventory', path: '/trc/inventory/store', icon: '🏪' },
        { name: 'RFR', path: '/trc/inventory/rfr', icon: '🔧' },
        { name: 'Productivity', path: '/trc/productivity', icon: '📈' },
        { name: '━━ SPARES ━━', path: null, icon: '', disabled: true },
        { name: 'Spare Dashboard', path: '/trc/spare/dashboard', icon: '📊' },
        { name: 'Spare Master', path: '/trc/spare/master', icon: '📋' },
        { name: 'BOM', path: '/trc/spare/bom', icon: '📄' },
        { name: 'Part Request', path: '/trc/spare/part-request', icon: '📮' },
        { name: '━━ REPAIRS ━━', path: null, icon: '', disabled: true },
        { name: 'Repair Dashboard', path: '/trc/repair/dashboard', icon: '📊' },
        { name: 'PNA Queue', path: '/trc/pna-queue', icon: '🔧' },
        { name: 'PNA Approvals', path: '/approver-review-new', icon: '✔️' },
        { name: '━━ ASC ━━', path: null, icon: '', disabled: true },
        { name: 'ASC Dashboard', path: '/trc/asc/dashboard', icon: '📊' },
        { name: 'ASC Partners', path: '/asc/manage', icon: '🤝' },
        { name: 'Create Dispatch', path: '/trc/asc/create-dispatch', icon: '📤' },
        { name: 'Track Dispatches', path: '/asc/dispatches', icon: '📍' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // BUYBACK MANAGEMENT
    // ═══════════════════════════════════════════════
    { 
      name: 'Buyback', 
      path: '/buyback', 
      icon: '📱',
      subItems: [
        { name: 'Dashboard', path: '/buyback-dashboard', icon: '📊' },
        { name: 'All Leads', path: '/buyback-leads', icon: '📋' },
        { name: 'Website QC Widget', path: '/sell', icon: '🌐', external: true },
        { name: 'Engineer Leaderboard', path: '/admin/engineer-leaderboard', icon: '🏆' },
        { name: 'Live Tracking', path: '/admin/engineer-tracking', icon: '📍' },
        { name: 'Engineer App', path: '/engineer', icon: '👷', external: true },
        { name: '━━ OTHER ━━', path: null, icon: '', disabled: true },
        { name: 'Price Comparison', path: '/buyback-price-comparison', icon: '📈' },
        { name: 'WhatsApp Templates', path: '/buyback-whatsapp', icon: '💬' },
        { name: 'Lead Sources', path: '/buyback-sources', icon: '🔗' },
        { name: 'Pricing Master', path: '/buyback-pricing', icon: '💰' },
      ]
    },
    { 
      name: 'Warehouse', 
      path: '/warehouse', 
      icon: '🏭',
      subItems: [
        { name: 'Warehouse Dashboard', path: '/warehouse/dashboard', icon: '📊' },
        { name: '━━ INWARD ━━', path: null, icon: '', disabled: true },
        { name: 'Create GRN', path: '/warehouse/create-grn', icon: '➕' },
        { name: 'Buyback Manifests', path: '/warehouse/manifests', icon: '📋' },
        { name: 'GRN from Manifest', path: '/warehouse/grn', icon: '📥' },
        { name: 'DID Label Printing', path: '/warehouse/did-labels', icon: '🏷️' },
        { name: '━━ OPERATIONS ━━', path: null, icon: '', disabled: true },
        { name: 'Bin Master', path: '/warehouse/bin-master', icon: '📦' },
        { name: 'Create STN', path: '/warehouse/stn', icon: '🚚' },
        { name: '━━ REPORTS ━━', path: null, icon: '', disabled: true },
        { name: 'Warehouse Reports', path: '/warehouse/reports', icon: '📈' },
        { name: '━━ SETTINGS ━━', path: null, icon: '', disabled: true },
        { name: 'Label Configuration', path: '/warehouse/label-config', icon: '⚙️' },
      ]
    },
    { 
      name: 'Masters', 
      path: '/masters', 
      icon: '🗂️',
      subItems: [
        { name: 'Bin Master', path: '/bin-master', icon: '📦' },
        { name: 'Engineer Master', path: '/masters/engineer', icon: '👨‍🔧' },
        { name: 'Category Master', path: '/masters/category', icon: '📑' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // FRANCHISEE & STORE OPERATIONS
    // ═══════════════════════════════════════════════
    { 
      name: 'Franchisee', 
      path: '/franchisee', 
      icon: '🏬',
      subItems: [
        { name: 'Leads', path: '/franchisee?main=leads&sub=all-leads', icon: '🎯' },
        { name: 'Journey Pipeline', path: '/franchisee?main=journey&sub=timeline', icon: '🛤️' },
        { name: 'Store Setup', path: '/franchisee?main=setup&sub=overview', icon: '🚀' },
        { name: 'Active Franchisees', path: '/franchisee?main=active&sub=franchisees', icon: '🏪' },
        { name: 'Franchisee Plans', path: '/franchisee?main=active&sub=plans', icon: '📋' },
        { name: 'Performance', path: '/franchisee?main=active&sub=performance', icon: '📊' },
        { name: 'Daily Operations', path: '/franchisee?main=active&sub=daily-ops', icon: '📅' },
        { name: 'Commissions', path: '/franchisee?main=active&sub=commissions', icon: '💰' },
      ]
    },
    { 
      name: 'Store Operations', 
      path: '/store-operations', 
      icon: '🏪',
      subItems: [
        { name: 'Dashboard', path: '/store-operations?tab=dashboard', icon: '📊' },
        { name: 'Daily Sales', path: '/store-operations?tab=daily-sales', icon: '💰' },
        { name: 'GRN & Stock', path: '/store-operations?tab=grn', icon: '📦' },
        { name: 'Reconciliation', path: '/store-operations?tab=reconciliation', icon: '🧾' },
        { name: 'Reports', path: '/store-operations?tab=reports', icon: '📈' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // FINANCE & ACCOUNTING (MERGED)
    // ═══════════════════════════════════════════════
    { 
      name: 'Finance & Accounting', 
      path: '/accounting', 
      icon: '💰',
      subItems: [
        { name: '━━ ACCOUNTING ━━', path: null, icon: '', disabled: true },
        { name: 'Accounting Dashboard', path: '/accounting?tab=dashboard', icon: '📊' },
        { name: 'Chart of Accounts', path: '/accounting?tab=coa', icon: '📋' },
        { name: 'Ledgers', path: '/accounting?tab=ledgers', icon: '📄' },
        { name: 'Vouchers', path: '/accounting?tab=vouchers', icon: '💳' },
        { name: 'Cost Centers', path: '/accounting?tab=cost-centers', icon: '🏢' },
        { name: 'Budgets', path: '/accounting?tab=budgets', icon: '📈' },
        { name: '━━ RECEIVABLES/PAYABLES ━━', path: null, icon: '', disabled: true },
        { name: 'Finance Dashboard', path: '/finance-management?tab=overview', icon: '📊' },
        { name: 'Payment Demands', path: '/finance-management?tab=demands', icon: '💰' },
        { name: 'Collections', path: '/finance-management?tab=collections', icon: '💵' },
        { name: '━━ REPORTS ━━', path: null, icon: '', disabled: true },
        { name: 'Trial Balance', path: '/accounting?tab=reports&report=trial-balance', icon: '📉' },
        { name: 'P&L Statement', path: '/accounting?tab=reports&report=pnl', icon: '📈' },
        { name: 'Balance Sheet', path: '/accounting?tab=reports&report=balance-sheet', icon: '📑' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // B2C WEBSITE & CMS
    // ═══════════════════════════════════════════════
    { 
      name: 'B2C Website', 
      path: '/b2c-cms', 
      icon: '🌐',
      subItems: [
        { name: 'CMS Dashboard', path: '/b2c-cms', icon: '📊' },
        { name: 'Banners', path: '/b2c-cms?tab=banners', icon: '🖼️' },
        { name: 'Testimonials', path: '/b2c-cms?tab=testimonials', icon: '💬' },
        { name: 'News & Press', path: '/b2c-cms?tab=news', icon: '📰' },
        { name: 'Categories', path: '/b2c-cms?tab=categories', icon: '📁' },
        { name: 'Email Templates', path: '/email-templates', icon: '📧' },
      ]
    },
    
    // ═══════════════════════════════════════════════
    // LEGAL & ADMIN
    // ═══════════════════════════════════════════════
    { 
      name: 'Legal & Compliance', 
      path: '/legal', 
      icon: '⚖️',
      subItems: [
        { name: 'Overview', path: '/legal', icon: '📊' },
        { name: 'Franchisee Agreements', path: '/legal?tab=franchisee', icon: '📜' },
        { name: 'Cases & Disputes', path: '/legal?tab=cases', icon: '⚠️' },
      ]
    },
  ];

  // Filter navigation based on user role
  const userRole = user?.role || 'Employee';
  
  // For Employees, use simplified self-service navigation
  const navigation = userRole === 'Employee' 
    ? getEmployeeSelfServiceNav() 
    : getNavigationForRole(userRole, fullNavigation);

  const isActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } text-white transition-all duration-300 overflow-hidden`}
        style={{backgroundColor: '#1a1f3a'}}
        data-testid="sidebar"
      >
        <div className="p-4">
          <div className="mb-8">
            {sidebarOpen ? (
              <div className="flex flex-col items-center">
                <div 
                  className="w-full py-6 px-4 rounded-lg border-2 flex items-center justify-center"
                  style={{
                    backgroundColor: '#1a1f3a',
                    borderColor: '#2b3380'
                  }}
                >
                  <span className="text-3xl font-bold tracking-widest" style={{color: '#ffffff'}}>
                    ZOBOX
                  </span>
                </div>
                <button
                  onClick={toggleSidebar}
                  className="text-white hover:text-gray-300 mt-4"
                  data-testid="toggle-sidebar-button"
                >
                  ◀
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div 
                  className="w-full py-4 px-2 rounded-lg border-2 flex items-center justify-center"
                  style={{
                    backgroundColor: '#1a1f3a',
                    borderColor: '#2b3380'
                  }}
                >
                  <span className="text-xl font-bold" style={{color: '#ffffff'}}>
                    ZO
                  </span>
                </div>
                <button
                  onClick={toggleSidebar}
                  className="text-white hover:text-gray-300 mt-2"
                  data-testid="toggle-sidebar-button"
                >
                  ▶
                </button>
              </div>
            )}
          </div>

          <nav ref={sidebarScrollRef} className="space-y-2 overflow-y-auto max-h-[calc(100vh-200px)] scroll-smooth" style={{ scrollBehavior: 'auto' }}>
            {navigation.map((item) => (
              <div key={item.path}>
                {item.subItems ? (
                  // Parent menu with sub-items
                  <div>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className="flex items-center justify-between w-full p-3 rounded-lg transition duration-200"
                      style={{
                        backgroundColor: expandedMenus[item.name] ? '#2b3380' : 'transparent',
                      }}
                      onMouseEnter={(e) => !expandedMenus[item.name] && (e.target.style.backgroundColor = '#2b338033')}
                      onMouseLeave={(e) => !expandedMenus[item.name] && (e.target.style.backgroundColor = 'transparent')}
                    >
                      <div className="flex items-center flex-1">
                        <span className="text-2xl">{item.icon}</span>
                        {sidebarOpen && (
                          <div className="ml-3 flex flex-col flex-1">
                            <span>{item.name}</span>
                            {item.badge && (
                              <span className="text-xs text-yellow-400 mt-0.5">{item.badge}</span>
                            )}
                          </div>
                        )}
                      </div>
                      {sidebarOpen && (
                        <span className="text-sm ml-2">{expandedMenus[item.name] ? '▼' : '▶'}</span>
                      )}
                    </button>
                    {expandedMenus[item.name] && sidebarOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          subItem.isSection ? (
                            // Nested section with its own sub-items
                            <div key={subItem.name} className="mt-2">
                              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">
                                {subItem.name}
                              </div>
                              <div className="ml-2 mt-1 space-y-1">
                                {subItem.subItems.map((nestedItem) => (
                                  <Link
                                    key={nestedItem.path}
                                    to={nestedItem.path}
                                    className="flex items-center p-2 rounded-lg transition duration-200 text-sm"
                                    style={{
                                      backgroundColor: isActive(nestedItem.path) ? '#3d4699' : 'transparent',
                                    }}
                                    onMouseEnter={(e) => !isActive(nestedItem.path) && (e.target.style.backgroundColor = '#2b338033')}
                                    onMouseLeave={(e) => !isActive(nestedItem.path) && (e.target.style.backgroundColor = 'transparent')}
                                  >
                                    <span className="text-lg">{nestedItem.icon}</span>
                                    <span className="ml-2">{nestedItem.name}</span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          ) : (
                            // Regular sub-item
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="flex items-center p-2 rounded-lg transition duration-200 text-sm"
                              style={{
                                backgroundColor: isActive(subItem.path) ? '#3d4699' : 'transparent',
                              }}
                              onMouseEnter={(e) => !isActive(subItem.path) && (e.target.style.backgroundColor = '#2b338033')}
                              onMouseLeave={(e) => !isActive(subItem.path) && (e.target.style.backgroundColor = 'transparent')}
                            >
                              <span className="text-lg">{subItem.icon}</span>
                              <span className="ml-2">{subItem.name}</span>
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Regular menu item
                  <Link
                    to={item.path}
                    className="flex items-center p-3 rounded-lg transition duration-200"
                    style={{
                      backgroundColor: isActive(item.path) ? '#2b3380' : 'transparent',
                    }}
                    onMouseEnter={(e) => !isActive(item.path) && (e.target.style.backgroundColor = '#2b338033')}
                    onMouseLeave={(e) => !isActive(item.path) && (e.target.style.backgroundColor = 'transparent')}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                  >
                    <span className="text-2xl">{item.icon}</span>
                    {sidebarOpen && <span className="ml-3">{item.name}</span>}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-md p-4 flex justify-between items-center" data-testid="header">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Welcome, {user?.mobile_number}</h2>
            <p className="text-sm text-gray-600">Role: {user?.role}</p>
          </div>
          
          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Email Icon */}
            <div className="relative">
              <button
                onClick={() => navigate('/notifications')}
                className="relative w-10 h-10 rounded-lg flex items-center justify-center transition duration-200"
                style={{ backgroundColor: '#e8eaf6' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#c5cae9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#e8eaf6'}
                title="Email Notifications"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="#2b3380" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {emailCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {emailCount > 9 ? '9+' : emailCount}
                  </span>
                )}
              </button>
            </div>

            {/* Tasks Icon */}
            <div className="relative task-dropdown-container">
              <button
                onClick={handleTaskClick}
                className="relative w-10 h-10 rounded-lg flex items-center justify-center transition duration-200"
                style={{ backgroundColor: '#e8f5e9' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#c8e6c9'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#e8f5e9'}
                title="My Tasks"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="#2e7d32" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {taskCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {taskCount > 9 ? '9+' : taskCount}
                  </span>
                )}
              </button>

              {/* Task Dropdown */}
              {showTaskDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold" style={{color: '#2b3380'}}>My Tasks ({taskCount})</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {myTasks.length > 0 ? (
                      myTasks.map((task, idx) => (
                        <div 
                          key={idx}
                          className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setShowTaskDropdown(false);
                            navigate('/tasks');
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <span className={`text-lg px-2 py-1 rounded ${
                              task.priority === 'high' ? 'bg-red-100 text-red-600' :
                              task.priority === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢'}
                            </span>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{task.title}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                Status: <span className="capitalize">{task.status?.replace('_', ' ')}</span>
                              </p>
                              {task.due_date && (
                                <p className="text-xs text-gray-400 mt-1">Due: {task.due_date}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <span className="text-4xl mb-2 block">✨</span>
                        <p>No pending tasks</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setShowTaskDropdown(false);
                        navigate('/tasks');
                      }}
                      className="w-full text-center text-sm font-medium py-2 rounded-lg"
                      style={{backgroundColor: '#2b3380', color: 'white'}}
                    >
                      View All Tasks
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications Bell */}
            <div className="relative notification-dropdown-container">
              <button
                onClick={handleNotificationClick}
                className="relative w-10 h-10 rounded-lg flex items-center justify-center transition duration-200"
                style={{ backgroundColor: '#fff3e0' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#ffe0b2'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#fff3e0'}
                title="Notifications"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="#e65100" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotificationDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold" style={{color: '#2b3380'}}>Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notification, idx) => (
                        <div 
                          key={idx}
                          className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setShowNotificationDropdown(false);
                            navigate('/notifications');
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">
                              {notification.type === 'attendance' ? '📅' : 
                               notification.type === 'leave' ? '🏖️' : 
                               notification.type === 'meeting' ? '📅' : 
                               notification.type === 'email' ? '📧' : '📢'}
                            </span>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{notification.title || 'New Notification'}</p>
                              <p className="text-xs text-gray-600 mt-1">{notification.message || notification.description || 'You have a new notification'}</p>
                              <p className="text-xs text-gray-400 mt-1">{notification.created_at ? new Date(notification.created_at).toLocaleString() : 'Just now'}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <span className="text-4xl mb-2 block">🔕</span>
                        <p>No new notifications</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-t border-gray-200">
                    <button
                      onClick={handleViewAllNotifications}
                      className="w-full text-center py-2 rounded-lg font-medium transition duration-200 hover:bg-gray-100"
                      style={{color: '#2b3380'}}
                    >
                      View All Notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* My Account Button */}
            <a
              href="/my-account"
              className="h-10 px-4 rounded-lg transition duration-200 flex items-center gap-2 text-sm font-medium"
              style={{ backgroundColor: '#e8eaf6', color: '#2b3380' }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#c5cae9'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#e8eaf6'; }}
              data-testid="my-account-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              My Account
            </a>

            {/* Logout Button */}
            <button
              onClick={onLogout}
              className="h-10 px-4 text-white rounded-lg transition duration-200 flex items-center gap-2 text-sm font-medium"
              style={{ backgroundColor: '#2b3380' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#1a237e'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#2b3380'}
              data-testid="logout-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto bg-gray-50 flex flex-col">
          <div className="flex-1">{children}</div>
          <Footer />
        </main>
      </div>
    </div>
  );
}

export default Layout;
